import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TemplateSelector } from '../../../shared/components/template-selector/template-selector.component';
import { ApiService } from '../../../_services/api.service';
import { Template } from '../../../_interfaces/template';
import { Dictation } from '../../../_interfaces/dictation';
import { CopilotDocument } from '../../../_interfaces/document';
import { AnalyticsService } from '../../../_services/analytics.service';
import { CopilotProcessingService } from '../../../_services/copilot-processing.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APP_DIALOG_DATA } from '../../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../../shared/components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../../shared/components/dialog/dialog.imports';

export type UpdateDocumentsDialogResult =
  | { action: 'update'; templates: Template[] }
  | { action: 'skip' };

export interface UpdateDocumentsDialogData {
  dictation: Dictation;
  templates?: Template[];
}

@Component({
  selector: 'app-update-documents',
  standalone: true,
  imports: [
    CommonModule,
    ...DIALOG_IMPORTS,
    MatButtonModule,
    MatIconModule,
    TemplateSelector,
    TranslateModule,
    LoadingSpinnerComponent,
  ],
  templateUrl: './update-documents.component.html',
  styleUrl: './update-documents.component.scss',
  host: {
    class: 'flex flex-col h-[90dvh] max-h-full overflow-hidden p-6 box-border',
  },
})
export class UpdateDocumentsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);

  allTemplates: Template[] = [];
  consultationTemplates: Template[] = [];
  addedNewTemplates: Template[] = [];
  selectedTemplates: Template[] = [];
  pickerSelectedTemplates: Template[] = [];
  disabledTemplates: Template[] = [];
  approvedTemplates: Template[] = [];
  showAddNewPicker = false;
  private consultationTemplateKeys = new Set<string>();
  isLoading = false;
  isSaving = false;
  dictation: Dictation;

  constructor(
    @Inject(APP_DIALOG_DATA) public data: UpdateDocumentsDialogData,
    private dialogRef: AppDialogRef<UpdateDocumentsComponent, UpdateDocumentsDialogResult>,
    private apiService: ApiService,
    private processing: CopilotProcessingService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
  ) {
    this.dictation = data.dictation;
  }

  get updateListTemplates(): Template[] {
    return [...this.consultationTemplates, ...this.addedNewTemplates];
  }

  get availableNewTemplates(): Template[] {
    return this.allTemplates.filter(
      (template) => !this.consultationTemplateKeys.has(this.templateKey(template)),
    );
  }

  get updateSelectionKey(): string {
    return `update-${this.updateListTemplates.length}-${this.selectedTemplates.length}`;
  }

  async ngOnInit(): Promise<void> {
    if (this.data.templates) {
      this.processTemplates(this.data.templates);
    } else {
      await this.loadTemplates();
    }
  }

  private templateKey(template: Pick<Template, 'id' | 'type'>): string {
    return `${String(template.id)}-${template.type}`;
  }

  private docKey(doc: CopilotDocument): string {
    return `${String(doc.templateId)}-${doc.templateType}`;
  }

  private toTemplate(doc: CopilotDocument): Template {
    return {
      id: doc.templateId,
      type: doc.templateType,
      title: doc.templateTitle,
      workflow_trigger: doc.workflow_trigger,
    } as Template;
  }

  private processTemplates(allTemplates: Template[]): void {
    this.allTemplates = allTemplates || [];

    const processed = (this.dictation.copilot?.documents?.processed ?? []).filter((doc) => !doc._delete);

    const existingByKey = new Map<string, CopilotDocument>();
    for (const doc of processed) {
      existingByKey.set(this.docKey(doc), doc);
    }

    this.consultationTemplateKeys = new Set(existingByKey.keys());
    this.consultationTemplates = this.allTemplates.filter((template) =>
      this.consultationTemplateKeys.has(this.templateKey(template)),
    );

    const approved: Template[] = [];
    const editableSelected: Template[] = [];
    const disabled: Template[] = [];

    for (const doc of processed) {
      const template = this.toTemplate(doc);
      if (doc.approved) {
        approved.push(template);
        disabled.push(template);
      } else {
        editableSelected.push(template);
      }
    }

    this.approvedTemplates = approved;
    this.disabledTemplates = disabled;
    this.selectedTemplates = [...editableSelected];
  }

  private async loadTemplates(): Promise<void> {
    this.isLoading = true;
    try {
      const allTemplates = await this.apiService.getCopilotTemplates();
      this.processTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      this.allTemplates = [];
      this.consultationTemplates = [];
      this.consultationTemplateKeys = new Set();
    } finally {
      this.isLoading = false;
    }
  }

  onUpdateSelectionChange(templates: Template[]): void {
    // Unchecking here only affects what gets regenerated; templates stay in the list.
    this.selectedTemplates = templates;
  }

  onPickerSelectionChange(templates: Template[]): void {
    // Defer merging into the update list until the user confirms the selection, so the
    // picker list itself never changes (no reordering, supports multi-select).
    this.pickerSelectedTemplates = templates;
  }

  openAddNewTemplatesStep(): void {
    // Pre-check templates already added so re-opening keeps them selected.
    this.pickerSelectedTemplates = [...this.addedNewTemplates];
    this.showAddNewPicker = true;
  }

  closeAddNewTemplatesStep(): void {
    this.addedNewTemplates = [...this.pickerSelectedTemplates];

    const addedKeys = new Set(this.addedNewTemplates.map((template) => this.templateKey(template)));
    const consultationSelected = this.selectedTemplates.filter(
      (template) =>
        this.consultationTemplateKeys.has(this.templateKey(template)) &&
        !addedKeys.has(this.templateKey(template)),
    );

    this.selectedTemplates = [...consultationSelected, ...this.addedNewTemplates];
    this.showAddNewPicker = false;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSkipForNow(): void {
    this.dialogRef.close({ action: 'skip' });
  }

  async onUpdateSelected(): Promise<void> {
    if (this.selectedTemplates.length === 0) {
      this.snackBar.open(
        this.translate.instant('Please select at least one blueprint.'),
        this.translate.instant('Close'),
        { duration: 3000 },
      );
      return;
    }

    this.isSaving = true;
    try {
      const fullTemplates = await Promise.all(
        this.selectedTemplates.map((template) =>
          this.apiService.getTemplate(template.id.toString(), template.type).catch(() => template),
        ),
      );

      if (!navigator.onLine) {
        this.snackBar.open(
          this.translate.instant(
            'You are offline. Documents will be updated when internet connection is restored.',
          ),
          this.translate.instant('Close'),
          { duration: 5000 },
        );
      }

      this.analytics.track('update_documents_clicked', {
        encounter_id: this.dictation._id,
        document_count: fullTemplates.length,
      });
      this.processing.enqueue(this.dictation, fullTemplates, 'update');
      const addedKeys = new Set(this.addedNewTemplates.map((template) => this.templateKey(template)));
      const createdTemplates = fullTemplates.filter((template) =>
        addedKeys.has(this.templateKey(template)),
      );
      for (const template of createdTemplates) {
        this.analytics.track('document_created', {
          encounter_id: this.dictation._id,
          blueprint_id: String(template.id),
          surface: 'encounter',
          trigger: 'resume',
        });
      }
      this.dialogRef.close({ action: 'update', templates: fullTemplates });
    } catch (error) {
      console.error('Error updating documents:', error);
      this.snackBar.open(
        this.translate.instant('Failed to update documents. Please try again.'),
        this.translate.instant('Close'),
        { duration: 3000 },
      );
    } finally {
      this.isSaving = false;
    }
  }
}
